'use client';

import { useCallback, useReducer, useRef } from 'react';

export type RainaExpression =
  | 'neutral'
  | 'happy'
  | 'thinking'
  | 'surprised'
  | 'talking'
  | 'listening'
  | 'concerned'
  | 'proud'
  | 'confused'
  | 'tired'
  | 'curious'
  | 'apologetic'
  | 'impressed'
  | 'focused'
  | 'excited'
  | 'celebrating'
  | 'patient'
  | 'skeptical';

export type RainaAnimation = 'wave' | 'nod' | 'blink' | 'headTilt' | 'earTwitch' | 'idle';

/** Default expression when no expression is specified. */
export const DEFAULT_EXPRESSION: RainaExpression = 'happy';

// Durations must match CSS keyframe definitions in raina.css
export const ANIMATION_DURATIONS: Record<Exclude<RainaAnimation, 'idle'>, number> = {
  wave: 1200,
  nod: 700,
  blink: 250,
  headTilt: 1000,
  earTwitch: 300,
};

/** Concurrency matrix — which animations can overlap. */
const CONCURRENT_ANIMATIONS: Record<RainaAnimation, RainaAnimation[]> = {
  idle: ['blink', 'earTwitch', 'headTilt'],
  wave: ['blink'],
  nod: ['blink'],
  blink: ['wave', 'nod', 'headTilt', 'earTwitch'],
  headTilt: ['blink', 'earTwitch'],
  earTwitch: ['blink', 'headTilt', 'wave', 'nod'],
};

export interface AnimationState {
  waving: boolean;
  blinking: boolean;
  nodding: boolean;
  earTwitching: boolean;
  headTilting: boolean;
  headTiltDirection: 'left' | 'right';
  queue: RainaAnimation[];
}

export type AnimationAction =
  | { type: 'START'; animation: RainaAnimation; direction?: 'left' | 'right' }
  | { type: 'END'; animation: RainaAnimation }
  | { type: 'QUEUE'; animation: RainaAnimation }
  | { type: 'PROCESS_QUEUE' };

const initialState: AnimationState = {
  waving: false,
  blinking: false,
  nodding: false,
  earTwitching: false,
  headTilting: false,
  headTiltDirection: 'right',
  queue: [],
};

function getActiveAnimations(state: AnimationState): RainaAnimation[] {
  const active: RainaAnimation[] = [];
  if (state.waving) active.push('wave');
  if (state.blinking) active.push('blink');
  if (state.nodding) active.push('nod');
  if (state.earTwitching) active.push('earTwitch');
  if (state.headTilting) active.push('headTilt');
  return active.length === 0 ? ['idle'] : active;
}

function canStartAnimation(state: AnimationState, animation: RainaAnimation): boolean {
  const active = getActiveAnimations(state);

  for (const activeAnim of active) {
    if (activeAnim === 'idle') continue;
    if (!CONCURRENT_ANIMATIONS[animation]?.includes(activeAnim)) {
      return false;
    }
  }
  return true;
}

function animationReducer(state: AnimationState, action: AnimationAction): AnimationState {
  switch (action.type) {
    case 'START': {
      const { animation, direction } = action;
      switch (animation) {
        case 'wave':
          return { ...state, waving: true };
        case 'blink':
          return { ...state, blinking: true };
        case 'nod':
          return { ...state, nodding: true };
        case 'earTwitch':
          return { ...state, earTwitching: true };
        case 'headTilt':
          return {
            ...state,
            headTilting: true,
            headTiltDirection: direction || 'right',
          };
        default:
          return state;
      }
    }

    case 'END': {
      const { animation } = action;
      let newState: AnimationState;
      switch (animation) {
        case 'wave':
          newState = { ...state, waving: false };
          break;
        case 'blink':
          newState = { ...state, blinking: false };
          break;
        case 'nod':
          newState = { ...state, nodding: false };
          break;
        case 'earTwitch':
          newState = { ...state, earTwitching: false };
          break;
        case 'headTilt':
          newState = { ...state, headTilting: false };
          break;
        default:
          newState = state;
      }
      // Process queue after animation ends
      if (newState.queue.length > 0) {
        const next = newState.queue[0];
        const rest = newState.queue.slice(1);
        if (next && canStartAnimation(newState, next)) {
          return animationReducer({ ...newState, queue: rest }, { type: 'START', animation: next });
        }
      }
      return newState;
    }

    case 'QUEUE':
      // Don't queue duplicates
      if (state.queue.includes(action.animation)) return state;
      return { ...state, queue: [...state.queue, action.animation] };

    case 'PROCESS_QUEUE': {
      if (state.queue.length === 0) return state;
      const next = state.queue[0];
      const rest = state.queue.slice(1);
      if (next && canStartAnimation(state, next)) {
        return animationReducer({ ...state, queue: rest }, { type: 'START', animation: next });
      }
      return state;
    }

    default:
      return state;
  }
}

export interface UseAnimationStateResult {
  state: AnimationState;
  dispatch: React.Dispatch<AnimationAction>;
  canStartAnimation: (animation: RainaAnimation) => boolean;
}

/**
 * Hook for managing Raina's animation state machine.
 *
 * Handles animation concurrency rules, queueing, and state transitions.
 */
export function useAnimationState(): UseAnimationStateResult {
  const [state, dispatch] = useReducer(animationReducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Keep canStartAnimation stable to prevent trigger functions from recreating on every state change
  const canStartAnimationStable = useCallback((animation: RainaAnimation) => canStartAnimation(stateRef.current, animation), []);

  return {
    state,
    dispatch,
    canStartAnimation: canStartAnimationStable,
  };
}
